<template>
  <div>
    <!-- Already Submitted - Success/Failed/Submitted State -->
    <div 
      v-if="alreadySubmitted && !isExpired" 
      :class="getStatusSectionClass(status)" 
      class="status-section">
      <div class="status-content">
        <div class="status-header">
          <div 
            :class="getStatusIconClass(status)" 
            class="status-icon">
            <svg 
              v-if="status === 'Valid'" 
              width="32" 
              height="32" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22,4 12,14.01 9,11.01"/>
            </svg>
            <svg 
              v-else-if="status === 'Invalid'" 
              width="32" 
              height="32" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <circle 
                cx="12" 
                cy="12" 
                r="10"/>
              <line 
                x1="15" 
                y1="9" 
                x2="9" 
                y2="15"/>
              <line 
                x1="9" 
                y1="9" 
                x2="15" 
                y2="15"/>
            </svg>
            <svg 
              v-else-if="status === 'Submitted'" 
              width="32" 
              height="32" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14,2 14,8 20,8"/>
            </svg>
          </div>
          <h3 class="status-title">{{ getStatusTitle(status) }}</h3>
        </div>
        <p class="status-message">{{ getStatusMessage(status) }}</p>
      </div>
    </div>
  
    <!-- Expired State -->
    <div 
      v-if="isExpired" 
      class="status-section expired">
      <div class="status-content">
        <div class="status-header">
          <div class="status-icon error-icon">
            <svg 
              width="32" 
              height="32" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              viewBox="0 0 24 24">
              <circle 
                cx="12" 
                cy="12" 
                r="10"/>
              <line 
                x1="15" 
                y1="9" 
                x2="9" 
                y2="15"/>
              <line 
                x1="9" 
                y1="9" 
                x2="15" 
                y2="15"/>
            </svg>
          </div>
          <h3 class="status-title">Session Expired</h3>
        </div>
        <p class="status-message">Timeframe for E-invoice request expired.</p>
      </div>
    </div>
  </div>
</template>
  
  <script>
export default {
    name: 'StatusSection',
    props: {
        alreadySubmitted: {
            type: Boolean,
            required: true,
        },
        isExpired: {
            type: Boolean,
            required: true,
        },
        status: {
            type: String,
            default: 'Submitted',
        },
    },
    methods: {
        getStatusSectionClass(status) {
            const sectionMap = {
                Valid: 'success',
                Invalid: 'error',
                Submitted: 'submitted',
            };
            return sectionMap[status] || '';
        },
        getStatusIconClass(status) {
            const iconMap = {
                Valid: 'success-icon',
                Invalid: 'error-icon',
                Submitted: 'info-icon',
            };
            return iconMap[status] || '';
        },
        getStatusTitle(status) {
            const titleMap = {
                Valid: 'Receipt Successfully Validated!',
                Invalid: 'Receipt Validation Failed',
                Submitted: 'Receipt Submitted for Review',
            };
            return titleMap[status] || 'Validation Result';
        },
        getStatusMessage(status) {
            const messageMap = {
                Valid:
                    'Your receipt has been validated and approved. All information is correct and verified.',
                Invalid:
                    'Your receipt could not be validated. Please check the information and try again.',
                Submitted:
                    'Your receipt has been submitted and is being processed. We will notify you once validation is complete.',
            };
            return messageMap[status] || '';
        },
    },
};
</script>
  
  <style scoped>
.status-section {
    text-align: center;
    padding: 32px 24px;
    margin-bottom: 24px;
    border-radius: 12px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 2px solid;
}

.status-section.success {
    border-color: #10b981;
}
.status-section.error {
    border-color: #ef4444;
}
.status-section.submitted {
    border-color: #0000;
}
.status-section.expired {
    border-color: #ef4444;
}

.status-content {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.status-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 16px;
}

.status-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 0;
    flex-shrink: 0;
}

.success-icon {
    background: #dcfce7;
    color: #16a34a;
}
.error-icon {
    background: #fee2e2;
    color: #dc2626;
}
.info-icon {
    background: #e0f2fe;
    color: #2563eb;
}

.status-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: #1e293b;
}

.status-message {
    font-size: 1rem;
    color: #64748b;
    margin: 0;
}

@media (max-width: 768px) {
    .status-section {
        padding: 24px 16px;
    }

    .status-header {
        gap: 8px;
        flex-direction: row;
        align-items: center;
    }

    .status-icon {
        width: 48px;
        height: 48px;
    }

    .status-title {
        font-size: 1.25rem;
        line-height: 1.2;
    }
}

@media (max-width: 480px) {
    .status-header {
        gap: 6px;
    }

    .status-icon {
        width: 40px;
        height: 40px;
    }

    .status-title {
        font-size: 1.1rem;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/e-invoice/portal/components/StatusSection.vue